import { gql, useReactiveVar, useApolloClient } from "@apollo/client";
import { saveToFutureFulfillmentsVar, } from "../helpers/client";
import { SaveFulfillmentSettingsFragmentDoc, useUpdateFulfillmentMutation, useUpdateUserMutation, OtherUnfulfilledFulfillmentsFragmentDoc, ChooseSubscriptionScreenVariantFragmentDoc, UpdateVariantSelectFragmentDoc, UpdateUserFragmentDoc, UpdateProductSelectionFragmentDoc, ChooseProductFragmentDoc, } from "../types";
gql `
  mutation UpdateUser($id: ID!, $userInput: UserInput!) {
    userUpdate(id: $id, userInput: $userInput) {
      ...UpdateUser
    }
  }

  fragment UpdateUser on User {
    phoneNumber
  }

  fragment UpdateUserInput on User {
    ...UpdateUser
    address {
      ...ChooseSubscriptionScreenAddress
    }
  }

  fragment UpdateUserFulfillmentAddress on Fulfillment {
    address {
      ...ChooseSubscriptionScreenAddress
    }
  }
`;
export const useUpdateUser = ({ userId }) => {
    const client = useApolloClient();
    const [updateUserMutation, result] = useUpdateUserMutation();
    const wrappedFunction = async (userInput) => {
        const { address, phoneNumber } = userInput;
        const userData = client.readFragment({
            id: `User:${userId}`,
            fragment: UpdateUserFragmentDoc,
            fragmentName: "UpdateUser",
        });
        if (!userData)
            return;
        return await updateUserMutation({
            variables: {
                id: userId,
                userInput: {
                    address,
                    phoneNumber: phoneNumber !== null && phoneNumber !== void 0 ? phoneNumber : userData.phoneNumber,
                },
            },
            update: (cache, { data }) => {
                // ... existing code ...
            },
        });
    };
    return { wrapped: wrappedFunction, mutation: updateUserMutation, result: result };
};
gql `
  fragment SaveFulfillmentSettings on Fulfillment {
    id
    fulfillmentDate
    fulfillmentDateAdjustmentReason
    deliveryMethod
    benefit {
      id
      ...on SubscriptionBenefit {
        claimable
      }
      ...on WelcomeGiftBenefit {
        claimable
      }
      ...on DriveBenefit {
        claimable
      }
    }
    variant {
      ...ChooseSubscriptionScreenVariant
    }
    addons {
      ...ChooseSubscriptionScreenAddon
    }
    address {
      ...ChooseSubscriptionScreenAddress
    }
    productSelections {
      ...ChooseProductSelection
    }
    shippingNotes
    platformLineItems {
      quantity
      platformProductVariantId
      swapped
    }
    curated
  }

  fragment OtherUnfulfilledFulfillments on Membership {
    unfulfilledFulfillments: fulfillments(query: { isFulfilled: false }) {
      fulfillmentDate
      deliveryMethod
      id
      benefit {
        id
      }
      address {
        ...ChooseSubscriptionScreenAddress
      }
      variant {
        ...ChooseSubscriptionScreenVariant
      }
      addons {
        ...ChooseSubscriptionScreenAddon
      }
      productSelections {
        ...ChooseProductSelection
      }
      shippingNotes
    }
  }
`;
gql `
  mutation UpdateFulfillment(
    $id: ID!
    $variantInput: VariantInput
    $addressInput: AddressInput
    $addons: [AddonInput!]!
    $shippingNotes: String
    $platformLineItems: [PlatformLineItemInput!]
    $productSelections: [ProductSelectionInput!]
    $fulfillmentDate: String
    $fulfillmentDateAdjustmentReason: String
    $deliveryMethod: DeliveryMethod
    $claimedAt: String
  ) {
    fulfillmentUpdate(
      id: $id
      fulfillmentInput: {
        addons: $addons
        address: $addressInput
        variant: $variantInput
        shippingNotes: $shippingNotes
        productSelections: $productSelections
        platformLineItems: $platformLineItems
        fulfillmentDate: $fulfillmentDate
        fulfillmentDateAdjustmentReason: $fulfillmentDateAdjustmentReason
        deliveryMethod: $deliveryMethod
        claimedAt: $claimedAt
      }
    ) {
      id
      deliveryMethod
      addons {
        ...ChooseSubscriptionScreenAddon
      }
      variant {
        ...ChooseSubscriptionScreenVariant
      }
      address {
        ...ChooseSubscriptionScreenAddress
      }
      productSelections {
        ...ChooseProductSelection
      }
      shippingNotes
      fulfillmentDate
      fulfillmentDateAdjustmentReason
      membership {
        id
      }
    }
  }
`;
export const useSaveFulfillmentSettings = ({ id, includeDate = false }) => {
    const client = useApolloClient();
    const saveToFutureFulfillments = useReactiveVar(saveToFutureFulfillmentsVar);
    const [updateFulfillmentMutation, result] = useUpdateFulfillmentMutation();
    const wrappedFunction = async (options, addressOverride, deliveryMethodOverride) => {
        var _a, _b, _c, _d;
        const fulfillmentData = client.readFragment({
            id: `Fulfillment:${id}`,
            fragment: SaveFulfillmentSettingsFragmentDoc,
            fragmentName: "SaveFulfillmentSettings",
        });
        const productSelections = fulfillmentData.productSelections.map(productSelection => {
            var _a;
            return ({
                productId: productSelection.product.id,
                variantId: (_a = productSelection.variant) === null || _a === void 0 ? void 0 : _a.id,
            });
        });
        const result = await updateFulfillmentMutation(Object.assign(Object.assign({}, options), { variables: Object.assign(Object.assign({ id: id, variantInput: fulfillmentData.variant, addressInput: addressOverride || fulfillmentData.address, shippingNotes: fulfillmentData.shippingNotes, addons: fulfillmentData.addons, productSelections, platformLineItems: fulfillmentData.platformLineItems, deliveryMethod: deliveryMethodOverride || fulfillmentData.deliveryMethod }, (includeDate && {
                fulfillmentDate: fulfillmentData.fulfillmentDate,
                fulfillmentDateAdjustmentReason: fulfillmentData.fulfillmentDateAdjustmentReason
            })), (fulfillmentData.benefit.claimable && fulfillmentData.curated && {
                claimedAt: new Date().toISOString(),
            })) }));
        if (result) {
            const parentFulfillment = client.readFragment({
                id: `Fulfillment:${id}`,
                fragment: gql `
          fragment ParentFulfillment on Fulfillment {
            membership {
              id
            }
          }
        `,
            });
            const membershipData = ((_a = parentFulfillment === null || parentFulfillment === void 0 ? void 0 : parentFulfillment.membership) === null || _a === void 0 ? void 0 : _a.id) ? client.readFragment({
                id: `Membership:${parentFulfillment.membership.id}`,
                fragment: OtherUnfulfilledFulfillmentsFragmentDoc,
                fragmentName: "OtherUnfulfilledFulfillments",
            }) : null;
            const otherFulfillments = membershipData === null || membershipData === void 0 ? void 0 : membershipData.unfulfilledFulfillments;
            if (otherFulfillments) {
                if (saveToFutureFulfillments) {
                    // Future fulfillments
                    let futureFulfillmentIds = otherFulfillments.filter((f) => {
                        return new Date(f.fulfillmentDate) >= new Date(fulfillmentData.fulfillmentDate) &&
                            id !== f.id &&
                            f.benefit.id === fulfillmentData.benefit.id;
                    }).map(f => f.id);
                    for (const futureFulfillmentId of futureFulfillmentIds) {
                        const futureFulfillmentData = otherFulfillments.find(f => f.id === futureFulfillmentId);
                        await updateFulfillmentMutation(Object.assign(Object.assign({}, options), { variables: {
                                id: futureFulfillmentId,
                                variantInput: fulfillmentData.variant,
                                addressInput: addressOverride || fulfillmentData.address,
                                shippingNotes: fulfillmentData.shippingNotes,
                                addons: fulfillmentData.addons,
                                productSelections,
                                deliveryMethod: deliveryMethodOverride || fulfillmentData.deliveryMethod,
                            } }));
                    }
                }
                // Past fulfillments with missing information
                const pastFulfillments = otherFulfillments.filter(f => new Date(f.fulfillmentDate) < new Date(fulfillmentData.fulfillmentDate) &&
                    id !== f.id &&
                    f.benefit.id === fulfillmentData.benefit.id);
                for (const pastFulfillment of pastFulfillments) {
                    const isAddressMissing = (!pastFulfillment.address ||
                        !pastFulfillment.address.line1 ||
                        !pastFulfillment.address.city ||
                        !pastFulfillment.address.state ||
                        !pastFulfillment.address.zip ||
                        !pastFulfillment.address.country);
                    const isVariantMissing = !!((_b = fulfillmentData.variant) === null || _b === void 0 ? void 0 : _b.id) && !((_c = pastFulfillment.variant) === null || _c === void 0 ? void 0 : _c.id);
                    const oldProductSelections = pastFulfillment.productSelections.map(ps => {
                        var _a;
                        return ({
                            productId: ps.product.id,
                            variantId: (_a = ps.variant) === null || _a === void 0 ? void 0 : _a.id,
                        });
                    });
                    const isProductSelectionMissing = !!((_d = fulfillmentData.productSelections) === null || _d === void 0 ? void 0 : _d.length) && (oldProductSelections.some(ps => !ps.variantId) ||
                        productSelections.some(psNew => !oldProductSelections.some(psOld => psOld.productId === psNew.productId)));
                    const isDeliveryMethodMissing = !pastFulfillment.deliveryMethod;
                    if (!isAddressMissing && !isVariantMissing && !isProductSelectionMissing && !isDeliveryMethodMissing) {
                        continue;
                    }
                    const variables = {
                        id: pastFulfillment.id,
                        addressInput: pastFulfillment.address,
                        shippingNotes: pastFulfillment.shippingNotes,
                        addons: pastFulfillment.addons,
                        variantInput: pastFulfillment.variant,
                        productSelections: oldProductSelections,
                        deliveryMethod: pastFulfillment.deliveryMethod,
                    };
                    if (isAddressMissing) {
                        variables.addressInput = addressOverride || fulfillmentData.address;
                    }
                    if (isVariantMissing) {
                        variables.variantInput = fulfillmentData.variant;
                    }
                    if (isProductSelectionMissing) {
                        const oldSelectionsWithVariant = oldProductSelections.filter(ps => !!ps.variantId);
                        const oldProductIds = oldSelectionsWithVariant.map(ps => ps.productId);
                        const newSelectionsToAdd = productSelections.filter(ps => !oldProductIds.includes(ps.productId));
                        variables.productSelections = [...oldSelectionsWithVariant, ...newSelectionsToAdd];
                    }
                    if (isDeliveryMethodMissing) {
                        variables.deliveryMethod = deliveryMethodOverride || fulfillmentData.deliveryMethod;
                    }
                    await updateFulfillmentMutation(Object.assign(Object.assign({}, options), { variables }));
                }
            }
        }
        return result;
    };
    return {
        wrapped: wrappedFunction,
        original: updateFulfillmentMutation,
        result,
    };
};
export const useUpdateVariantSelect = (id) => {
    const client = useApolloClient();
    const updateVariantSelect = (ev) => {
        const variantData = client.readFragment({
            id: `Variant:${ev.target.value}`,
            fragment: ChooseSubscriptionScreenVariantFragmentDoc,
        });
        client.writeFragment({
            fragment: UpdateVariantSelectFragmentDoc,
            fragmentName: "UpdateVariantSelect",
            data: {
                __typename: "Fulfillment",
                id: id,
                variant: variantData,
            },
        });
    };
    return updateVariantSelect;
};
gql `
fragment UpdateProductSelection on Fulfillment {
  productSelections {
    ...ChooseProductSelection
  }
}
`;
export const useUpdateProductSelection = (productId, fulfillmentId) => {
    const client = useApolloClient();
    const updateProductSelection = (ev) => {
        if (!ev.target.value)
            return;
        const fulfillment = client.readFragment({
            id: `Fulfillment:${fulfillmentId}`,
            fragment: UpdateProductSelectionFragmentDoc,
            fragmentName: "UpdateProductSelection",
        });
        const productSelections = fulfillment.productSelections.map(productSelection => {
            if (productSelection.product.id === productId) {
                const variant = productSelection.product.variants.find(v => v.id === ev.target.value);
                return Object.assign(Object.assign({}, productSelection), { variant });
            }
            else {
                return productSelection;
            }
        });
        if (!productSelections.some((productSelection) => productSelection.product.id === productId)) {
            const variant = client.readFragment({
                id: `Variant:${ev.target.value}`,
                fragment: ChooseSubscriptionScreenVariantFragmentDoc,
                fragmentName: "ChooseSubscriptionScreenVariant",
            });
            const product = client.readFragment({
                id: `Product:${productId}`,
                fragment: ChooseProductFragmentDoc,
                fragmentName: "ChooseProduct",
            });
            productSelections.push({
                "__typename": "ProductSelection",
                product,
                variant,
            });
        }
        client.writeFragment({
            id: `Fulfillment:${fulfillmentId}`,
            fragment: UpdateProductSelectionFragmentDoc,
            fragmentName: "UpdateProductSelection",
            data: {
                productSelections,
            }
        });
    };
    return updateProductSelection;
};
